import { capitalize } from 'utils/strings'

import type { IconName } from 'components/Icon'
import type { CompanySettingsRbacContextValue } from 'components/rbac-settings/company/CompanySettingsRbacContext'
import type { UserInfo } from 'types/api/companies/users'
import type { Company } from 'types/api/rbac/company'
import type { Team } from 'types/api/teams'

type SettingSectionMenuItem = {
  name: string
  url: string
  enabled: boolean
}

export type TeamSettingSectionMenuItem = Pick<
  SettingSectionMenuItem,
  'name'
> & {
  url?: string
}

/* Company Settings and User Settings */
export type SettingsSectionMenuOptions = {
  icon: IconName
  items: SettingSectionMenuItem[]
  sectionName: string
}

/* Team Settings */
export type TeamSettingsSectionMenuOptions = Pick<
  SettingsSectionMenuOptions,
  'icon' | 'sectionName'
> & {
  items: TeamSettingSectionMenuItem[]
}

export const getCompanySettingsMenuOptions = (
  companySettingsRbacContextValues: CompanySettingsRbacContextValue,
  company?: Company
): SettingsSectionMenuOptions => {
  const {
    canViewApiKeys,
    canViewUsers,
    canViewTeams,
    canEditRoles,
    canEditSecurityPrefs,
    canViewPlan
  } = companySettingsRbacContextValues

  const isApiKeysEnabled = Boolean(company?.auth_method_prefs?.api_keys_enabled)

  const shouldRenderApiKeysMenuItem =
    canViewApiKeys && canViewUsers && isApiKeysEnabled

  return {
    icon: 'solidBuilding',
    items: [
      {
        name: 'Options',
        url: '/settings/company/options',
        enabled: true
      },
      {
        name: 'Users',
        url: '/settings/company/users',
        enabled: canViewUsers
      },
      {
        name: 'Teams',
        url: '/settings/company/teams',
        enabled: canViewTeams
      },
      {
        name: 'Roles',
        url: '/settings/company/roles',
        enabled: canEditRoles
      },
      {
        name: 'Authentication',
        url: '/settings/company/authentication',
        enabled: canEditSecurityPrefs
      },
      {
        name: 'API Keys',
        url: '/settings/company/api-keys',
        enabled: shouldRenderApiKeysMenuItem
      },
      {
        name: 'Billing',
        url: '/settings/company/billing',
        enabled: canViewPlan
      },
      {
        name: 'Usage',
        url: '/settings/company/usage',
        enabled: canViewPlan
      }
    ].filter((item) => item.enabled),
    sectionName: 'Company'
  }
}

export const teamSettingsMenuRoutes = [
  ['Details', '/settings/team/:teamId/details'],
  ['Members', '/settings/team/:teamId/members'],
  ['Options', '/settings/team/:teamId/options'],
  ['Integrations', '/settings/team/:teamId/integrations'],
  ['Notifications', '/settings/team/:teamId/notifications']
] as const

export const getTeamSettingsMenuOptions = (
  items: TeamSettingSectionMenuItem[]
): TeamSettingsSectionMenuOptions => {
  return {
    icon: 'solidUserGroup',
    items,
    sectionName: 'Team'
  }
}

export enum UserSettingsMenuNames {
  Details = 'Details',
  Authentication = 'Authentication',
  APIKeys = 'API Keys',
  Integrations = 'Integrations'
}

export function formatBreadcrumbName(name: string): string {
  const acronyms = new Set(['API'])

  return name
    .split('-')
    .map((word) => {
      const upperCaseWord = word.toUpperCase()
      if (acronyms.has(upperCaseWord)) {
        return upperCaseWord
      }

      return capitalize(word)
    })
    .join(' ')
}

export function getBreadcrumbsForSettingsIndex(
  pathParts: string[],
  selectedTeamName?: string,
  teamId?: string
) {
  return pathParts.map((part, index) => ({
    name: part === teamId ? selectedTeamName : formatBreadcrumbName(part),
    icon: part === 'settings' ? 'gear' : undefined,
    to: `/${pathParts.slice(0, index + 1).join('/')}`
  }))
}

export function getPathParts(pathname: string) {
  return pathname.split('/').filter(Boolean)
}

export function getUserByEmail(email: string, users?: UserInfo[]) {
  return users?.find((user) => user.identifier === email)
}

export function getTeamById(teams: Team[], selectedTeamId = '') {
  if (!selectedTeamId) return

  return teams.find((team) => team.identifier === selectedTeamId)
}
